/**
 * Tab Navigation Component
 * 
 * Horizontal tab bar for navigating between main sections of the members area.
 * Supports tiered access control and AASB S2 Lab dropdown with BETA badge.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Home,
  BarChart3,
  Leaf,
  FileText,
  Target,
  FlaskConical,
  ChevronDown,
  Lock,
  Crown,
  ClipboardCheck,
  User,
  Shield,
  Scale,
  Users,
  Thermometer
} from 'lucide-react';

import { FEATURES } from '@/permissions/permissionsMatrix';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, route: '/dashboard', feature: FEATURES.DASHBOARD_VIEW },
  { id: 'emissions', label: 'Emissions', icon: BarChart3, route: '/entries', feature: FEATURES.EMISSIONS_VIEW },
  { id: 'offsets', label: 'Offsets', icon: Leaf, route: '/offsets', feature: FEATURES.OFFSETS_VIEW },
  { id: 'reports', label: 'Reports', icon: FileText, route: '/reports', feature: FEATURES.REPORTS_TABLE },
  { id: 'grants', label: 'Grants', icon: Target, route: '/grants', feature: FEATURES.GRANTS_SEARCH },
];

const AASB_S2_LAB_ITEMS = [
  { id: 'compliance', label: 'Compliance Check', icon: ClipboardCheck, route: '/compliance', description: 'AASB S2 reporting thresholds' },
  { id: 'governance', label: 'Governance', icon: User, route: '/governance', description: 'Responsible officer & oversight' },
  { id: 'risks', label: 'Climate Risks', icon: Shield, route: '/risks', description: 'Risk & opportunity register' },
  { id: 'materiality', label: 'Materiality', icon: Scale, route: '/materiality', description: 'ESG topic assessment' },
  { id: 'social-metrics', label: 'Social Metrics', icon: Users, route: '/social-metrics', description: 'Diversity & modern slavery' },
  { id: 'stress-test', label: 'Scenario Stress Test', icon: Thermometer, route: '/stress-test', description: '1.5°C vs 2.5°C resilience' },
];

export default function TabNavigation({
  activeTab = 'dashboard',
  subscriptionTier = 'demo',
  onUpgradeClick,
  canPerformBilling = false,
  getAccessResult = () => ({ allowed: true }),
  className = ''
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const dropdownRef = useRef(null);

  const isPro = subscriptionTier === 'pro';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLabDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine active tab from current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    if (path === '/entries') return 'emissions';
    if (path === '/offsets') return 'offsets';
    if (path === '/reports' || path === '/report-builder') return 'reports';
    if (path === '/grants') return 'grants';
    // Check AASB S2 Lab routes
    const labRoutes = ['/compliance', '/governance', '/risks', '/materiality', '/social-metrics', '/stress-test'];
    if (labRoutes.includes(path)) return 'aasb-s2-lab';
    return activeTab;
  };

  const currentTab = getActiveTab();

  const handleTabClick = (tab) => {
    if (tab.proOnly && !isPro) {
      setUpgradeFeature(tab.label);
      setShowUpgradeModal(true);
      return;
    }
    navigate(tab.route);
  };

  const handleLabItemClick = (item) => {
    const access = getAccessResult('aasb_s2_lab.view');
    if (!access.allowed) {
      setUpgradeFeature('AASB S2 Lab');
      setUpgradeTarget(access.upgradeTarget);
      setShowUpgradeModal(true);
      setShowLabDropdown(false);
      return;
    }
    navigate(item.route);
    setShowLabDropdown(false);
  };

  const handleLabButtonClick = () => {
    const access = getAccessResult('aasb_s2_lab.view');
    if (!access.allowed) {
      setUpgradeFeature('AASB S2 Lab');
      setUpgradeTarget(access.upgradeTarget);
      setShowUpgradeModal(true);
      return;
    }
    setShowLabDropdown(!showLabDropdown);
  };

  return (
    <>
      <div className={`bg-white ${className}`} data-testid="tab-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center space-x-1" aria-label="Main navigation">
            {/* Primary Tabs */}
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;
              const access = getAccessResult(tab.feature);
              const isLocked = !access.allowed;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (!access.allowed) {
                      if (access.upgradeRequired) {
                        setUpgradeFeature(tab.label);
                        setUpgradeTarget(access.upgradeTarget);
                        setShowUpgradeModal(true);
                      } else {
                        toast.error(`You don't have permission to access ${tab.label}.`);
                      }
                      return;
                    }
                    navigate(tab.route);
                  }}
                  className={`
                    flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-200
                    ${isActive
                      ? 'border-[#74A57F] text-[#003B5C]'
                      : 'border-transparent text-slate-600 hover:text-[#003B5C] hover:border-slate-300'
                    }
                    ${isLocked ? 'opacity-50 grayscale' : ''}
                  `}
                  data-testid={`tab-${tab.id}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {isLocked && <Lock className="w-3 h-3 text-slate-400" />}
                </button>
              );
            })}

            {/* AASB S2 Lab Dropdown */}
            <div className="relative" ref={dropdownRef}>
              {(() => {
                const access = getAccessResult('aasb_s2_lab.view');
                const isLocked = !access.allowed;

                return (
                  <button
                    onClick={() => {
                      if (!access.allowed) {
                        if (access.upgradeRequired) {
                          setUpgradeFeature('AASB S2 Lab');
                          setUpgradeTarget(access.upgradeTarget);
                          setShowUpgradeModal(true);
                        } else {
                          toast.error("You don't have permission to access AASB S2 Lab.");
                        }
                        return;
                      }
                      setShowLabDropdown(!showLabDropdown);
                    }}
                    className={`
                      flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                      border-b-2 transition-all duration-200
                      ${currentTab === 'aasb-s2-lab'
                        ? 'border-[#74A57F] text-[#003B5C]'
                        : 'border-transparent text-slate-600 hover:text-[#003B5C] hover:border-slate-300'
                      }
                      ${isLocked ? 'opacity-50 grayscale' : ''}
                    `}
                    data-testid="tab-aasb-s2-lab"
                  >
                    <FlaskConical className="w-4 h-4" />
                    <span>AASB S2 Lab</span>
                    <span className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded font-semibold">
                      BETA
                    </span>
                    {isLocked && <Lock className="w-3 h-3 text-slate-400" />}
                    {!isLocked && <ChevronDown className={`w-3 h-3 transition-transform ${showLabDropdown ? 'rotate-180' : ''}`} />}
                  </button>
                );
              })()}

              {/* Lab Dropdown Menu */}
              {showLabDropdown && (
                <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-xs text-slate-500">
                      AASB S2 readiness tools for Group 3 organisations preparing for FY 2027/28 mandatory reporting.
                    </p>
                  </div>
                  {AASB_S2_LAB_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.route;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleLabItemClick(item)}
                        className={`
                          w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 
                          flex items-center space-x-3
                          ${isActive ? 'bg-slate-50' : ''}
                        `}
                        data-testid={`lab-item-${item.id}`}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? 'text-[#74A57F]' : 'text-slate-500'}`} />
                        <div>
                          <div className={`font-medium ${isActive ? 'text-[#003B5C]' : 'text-slate-800'}`}>
                            {item.label}
                          </div>
                          <div className="text-xs text-slate-500">{item.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setShowUpgradeModal(false); setUpgradeTarget(null); }}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-[#003B5C] mb-2">{upgradeFeature}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {upgradeFeature === 'Offsets'
                  ? 'Track and allocate carbon offsets to demonstrate your commitment to net zero.'
                  : upgradeFeature === 'AASB S2 Lab'
                    ? 'Access advanced AASB S2 readiness tools to prepare for mandatory climate reporting in FY 2027/28.'
                    : upgradeTarget === 'standard_or_pro'
                      ? `Access ${upgradeFeature} and more by upgrading your plan.`
                      : `Access ${upgradeFeature} and more with our Pro plan.`
                }
              </p>
              <div className="bg-purple-50 rounded-lg p-3 mb-4">
                <p className="text-purple-700 text-sm font-medium">
                  {upgradeTarget === 'standard_or_pro' ? 'Available on Standard & Pro Plans' : 'Available on Pro Plan'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowUpgradeModal(false); setUpgradeTarget(null); }}
                >
                  Maybe Later
                </Button>
                <Button
                  className={`flex-1 ${!canPerformBilling ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600'}`}
                  onClick={() => {
                    if (!canPerformBilling) {
                      toast.error("You don't have permission to manage billing. Please contact your administrator.");
                      return;
                    }
                    setShowUpgradeModal(false);
                    setUpgradeTarget(null);
                    if (onUpgradeClick) {
                      onUpgradeClick();
                    } else {
                      window.location.href = '/billing';
                    }
                  }}
                >
                  {upgradeTarget === 'standard_or_pro' ? 'View Plans' : 'Upgrade to Pro'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
